
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ValidationProvider, extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import { JobComplianceDriver } from '@/models/JobComplianceTypes';
import store from '@/store';

extend('requiredDriver', {
  ...required,
  message: 'Required',
});

@Component({
  components: {
    ValidationProvider,
  },
})
export default class LookupDriver extends Vue {
  @Prop({ type: Array, required: true }) public driversData: JobComplianceDriver[];
  @Prop({ type: String, required: true }) public name: string;
  @Prop({ type: Boolean, required: false, default: false }) public disabled: boolean;

  public loading = false;
  public driverValue = '';
  public selectItems: string[] = [];
  public showOptions = false;
  public arrowIdx = -1;
  private driverChosen = {
    id: 0,
    code: 0,
    full_name: '',
    mobile_phone: '',
  };
  private driverChosenDefault = { ...this.driverChosen };

  @Watch('driverChosen', { deep: true })
  private watchServiceChosen() {
    this.$emit('changed', this.driverChosen);
  }

  private applyValueFromParent(item): void {
    this.driverChosen = item;
    this.showOptions = false;
    this.driverValue = item.code;
    this.$forceUpdate();
  }

  public handleInput(evt): void {
    if (evt.target.value) {
      this.driverValue = evt.target.value;
      const filteredAddresses = this.driversData.filter(
        x => `${x.code} - ${x.full_name}`.toUpperCase().indexOf(String(evt.target.value).toUpperCase()) >= 0,
      );
      this.selectItems = [...filteredAddresses.map(driver => `${driver.code} - ${driver.full_name}`)];
    }
  }

  public inputKeyPressed(evt: KeyboardEvent): void {
    // Open dropdown on Focus
    if (!this.showOptions) {
      this.showOptions = true;
      this.arrowIdx = -1;
    }
    // Close dropdown when "Escape" pressed
    if (evt.code === 'Escape') {
      this.showOptions = false;
      this.arrowIdx = -1;
    }
    // ArrowDown / ArrowUp pressed
    if (evt.code === 'ArrowDown' && this.arrowIdx < this.selectItems.length - 1) {
      this.arrowIdx += 1;
      this.fixScrolling();
    }
    if (evt.code === 'ArrowUp' && this.arrowIdx >= 1) {
      this.arrowIdx -= 1;
      this.fixScrolling();
    }
    // Enter pressed
    if (evt.code === 'Enter' && this.arrowIdx >= 0) {
      evt.preventDefault();
      const chosenService = this.driversData.find(
        x => `${x.code} - ${x.full_name}`.substring(0, 30) === this.selectItems[this.arrowIdx].substring(0, 30),
      );
      if (chosenService) {
        this.driverChosen = chosenService;
        this.showOptions = false;
        this.driverValue = this.selectItems[this.arrowIdx].split(' - ')[0];
        this.arrowIdx = -1;
      }
    }
  }

  public clearInput(): void {
    this.driverValue = '';
    this.showOptions = false;
    this.driverChosen = { ...this.driverChosenDefault };
    this.arrowIdx = -1;
    this.selectItems = [];
  }

  private fixScrolling() {
    const options = this.$refs.options as HTMLFormElement;
    const scrollContainer = this.$refs.scrollContainer as HTMLFormElement;
    if (options && scrollContainer) {
      const liH = options[this.arrowIdx].clientHeight;
      scrollContainer.scrollTop = liH * this.arrowIdx;
    }
  }

  public inputFocused(): void {
    this.showOptions = true;
    if (this.driverValue?.length) {
      this.handleInput({
        target: {
          value: this.driverValue,
        },
      });
    } else {
      this.selectItems = this.driversData.map(driver => `${driver.code} - ${driver.full_name}`);
    }
  }

  public inputBlured(): void {
    const codes = this.selectItems.map(x => x.split(' - ')[0]);
    const code = codes.find(x => x === String(this.driverValue));
    const idx = this.driversData.find(x => String(x.code) === code);
    if (idx) {
      this.driverChosen = { ...idx };
    }
    setTimeout(() => {
      this.showOptions = false;
    }, 200);
  }

  public handleItemClick(item: string): void {
    const driverChosen = this.driversData.find(
      x => `${x.code} - ${x.full_name}`.substring(0, 30) === item.substring(0, 30),
    );
    if (driverChosen) {
      this.driverChosen = driverChosen;
      this.showOptions = false;
      this.driverValue = item.split(' - ')[0];
    }
  }

  public clickedOutside(): void {
    this.showOptions = false;
  }

  public oneOfExtendValidationRule = extend('oneOfDiver', value => {
    return store.getters['moduleStaticData/getDrivers'].some(x => String(x.code) === String(value).toUpperCase())
      ? true
      : 'Not match';
  });
}
